import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    combineLatest,
    EMPTY,
    filter,
    map,
    Observable,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs';

import { parseScannedCode } from 'app/features/scan-flow/utils';
import {
    Credentials,
    defaultPersonalUserPreferences,
    PersonalUserPreferences,
    UserInfo,
} from 'app/shared/models';
import { selectApiConfigState } from 'app/state/api-config/api-config.selectors';
import { resetScannedCode, scanNewCode } from 'app/state/scanned-codes/scanned-codes.actions';
import { selectScannedCodeByToken } from 'app/state/scanned-codes/scanned-codes.selectors';
import {
    selectAppModeIfDefined,
    selectInputModeIfDefined,
    selectIsScanFlowAllowedIfDefined,
} from 'app/state/settings/settings.selectors';
import { selectUserSessionFromHistory } from 'app/state/user-session/user-session.selectors';

import { AuthService } from './services/auth.service';

export type SignInError = 'invalid-credentials' | 'technical-error';

export interface AuthenticatedUser {
    userInfo: UserInfo | undefined;
    credentials: Credentials;
    personalUserPreferences: PersonalUserPreferences;
}

export interface SigninState {
    credentials: Credentials | undefined;
    error: SignInError | undefined;
    forceScanIfPossible: boolean;
    inProgress: boolean;
    scanResult: { token: string; employeeNumber: number } | undefined;
    success: boolean;
    userInfo: UserInfo | undefined;
    personalUserPreferences: PersonalUserPreferences;
}

const initialState: SigninState = {
    credentials: undefined,
    error: undefined,
    forceScanIfPossible: false,
    inProgress: false,
    scanResult: undefined,
    success: false,
    userInfo: undefined,
    personalUserPreferences: defaultPersonalUserPreferences,
};

function newToken() {
    return 'employee_' + Date.now();
}

@Injectable()
export class SigninStore extends ComponentStore<SigninState> {
    constructor(
        private authService: AuthService,
        private store: Store,
    ) {
        super(initialState);
    }

    authenticating$ = this.select((state) => state.inProgress);
    authenticationError$ = this.select((state) => state.error);
    authenticatedUser$: Observable<AuthenticatedUser> = this.state$.pipe(
        filter((state) => state.success),
        map((state) => ({
            credentials: state.credentials ?? { employeeNumber: 0, pin: '' },
            userInfo: state.userInfo,
            personalUserPreferences: state.personalUserPreferences,
        })),
    );
    inputMode$ = this.store.pipe(selectInputModeIfDefined);
    appMode$ = this.store.pipe(selectAppModeIfDefined);
    scanResult$ = this.select((state) => state.scanResult);
    forceScanIfPossible$ = this.select((state) => state.forceScanIfPossible);

    isManualInputForbidden$ = combineLatest([
        this.forceScanIfPossible$,
        this.store.pipe(selectIsScanFlowAllowedIfDefined),
    ]).pipe(map(([forceScanIfPossible, isScanFlowAllowed]) => isScanFlowAllowed && forceScanIfPossible));

    // noinspection JSUnusedGlobalSymbols
    readonly signIn = this.effect((credentials$: Observable<Credentials>) => {
        return credentials$.pipe(
            tap((cred) => this.onStartingAuth(cred)),
            switchMap((credentials) => {
                return this.store.pipe(
                    select(selectUserSessionFromHistory(credentials.employeeNumber)),
                    map((previousUserSession) => ({ credentials, previousUserSession })),
                );
            }),
            withLatestFrom(this.store.pipe(select(selectApiConfigState))),
            switchMap(([{ credentials, previousUserSession }, { identifier, apiVersion }]) => {
                const personalUserPreferences = previousUserSession?.data ?? defaultPersonalUserPreferences;
                return this.authService.authenticate({ credentials, identifier, apiVersion }).pipe(
                    tap({
                        next: ({ isAuthenticated, userInfo }) => {
                            if (isAuthenticated) {
                                this.onSuccess({
                                    userInfo,
                                    personalUserPreferences,
                                });
                            } else {
                                this.onError('invalid-credentials');
                            }
                        },
                        error: () => {
                            if (previousUserSession?.credentials.pin) {
                                if (previousUserSession.credentials.pin === credentials.pin) {
                                    this.onSuccess({
                                        userInfo: previousUserSession.userInfo,
                                        personalUserPreferences,
                                    });
                                } else {
                                    this.onError('invalid-credentials');
                                }
                            } else {
                                this.onError('technical-error');
                            }
                        },
                    }),
                    catchError(() => EMPTY),
                );
            }),
        );
    });

    readonly scanBarcodeIfManualInputForbidden = this.effect((trigger$) => {
        return trigger$.pipe(
            withLatestFrom(this.isManualInputForbidden$, this.store.pipe(selectInputModeIfDefined)),
            filter(
                ([, isManualInputForbidden, inputMode]) => isManualInputForbidden && inputMode === 'barcode',
            ),
            tap(() => {
                this.scan();
            }),
            catchError(() => EMPTY),
        );
    });

    readonly scan = this.effect((trigger$) => {
        return trigger$.pipe(
            map(() => newToken()),
            tap((token) => {
                this.store.dispatch(
                    scanNewCode({ allowedCodeTypes: ['employee'], token, onScanFinishedAction: undefined }),
                );
            }),
            tap((token) => this.syncScannedEmployeeNumber(token)),
            catchError(() => EMPTY),
        );
    });

    readonly syncScannedEmployeeNumber = this.effect((token$: Observable<string>) =>
        token$.pipe(
            switchMap((token) =>
                this.store.pipe(
                    select(selectScannedCodeByToken(token)),
                    map((code) => ({ token, code })),
                ),
            ),
            filter(({ code }) => !!code),
            tap(({ token, code }) => {
                this.store.dispatch(resetScannedCode({ token }));
                this.processScannedCode({ code, token });
            }),
            catchError(() => EMPTY),
        ),
    );

    processScannedCode({ code, token }: { code: string; token?: string }) {
        const scannedCode = parseScannedCode({ code, allowedCodeTypes: ['employee'] });

        if (scannedCode?.number) {
            this.onEmployeeNumberScanned({ token, employeeNumber: scannedCode.number });
        }
    }

    private onSuccess = this.updater(
        (
            state,
            {
                userInfo,
                personalUserPreferences,
            }: { userInfo: UserInfo | undefined; personalUserPreferences: PersonalUserPreferences },
        ) => ({
            ...state,
            error: undefined,
            inProgress: false,
            success: true,
            userInfo,
            personalUserPreferences,
        }),
    );

    private onError = this.updater((state, error: SignInError) => ({
        ...state,
        error,
        inProgress: false,
        success: false,
    }));

    private onStartingAuth = this.updater((state, credentials: Credentials) => ({
        ...state,
        credentials,
        userInfo: undefined,
        inProgress: true,
        error: undefined,
        success: false,
    }));

    private onEmployeeNumberScanned = this.updater(
        (state, { token, employeeNumber }: { token?: string; employeeNumber: number }) => ({
            ...state,
            scanResult: { token: token || newToken(), employeeNumber },
        }),
    );

    updateForceScanIfPossible = this.updater((state, forceScanIfPossible: boolean) => ({
        ...state,
        forceScanIfPossible,
    }));
}
